import React, { PropsWithChildren, Suspense, useState } from 'react';
import { Flex } from 'glints-aries/es';
import { Icon, Popover, Typography } from 'glints-aries/es/@next';
import { Neutral, Red } from 'glints-aries/es/@next/utilities/colors';
import { space8 } from 'glints-aries/es/@next/utilities/spacing';
import { get } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import * as portals from 'react-reverse-portal';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import {
  displayUserName as userName,
  truncateString,
} from '../../../common/filters';
import { useGetFeatureEnabledForCountry } from '../../../common/hooks/useGetFeatureEnabledForCountry';
import useIsMonthlyQuotaEnabled from '../../../common/hooks/useIsMonthlyQuotaEnabled';
import ErrorBoundaryWithCrashReporting from '../../../components/Error/ErrorBoundaryWithCrashReporting';
import VIPProfilePicture from '../../../components/GlintsPicture/VIPProfilePicture';
import { useGlintsVIPMembershipInfo } from '../../../components/GlintsVIPMembershipInfo/hooks/useGlintsVIPMembershipInfo';
import RenderBasedOnCompanyCountry from '../../../components/RenderBasedOnCompanyCountry';
import { User } from '../../../models/User';
import { getUser } from '../../../selectors/user';
import CompanyShortlistQuotaMenuItem from '../../CreditSystem/NavbarUserMenu/CompanyShortlistQuotaMenuItem';
import CreditBalanceActivatorBadge from '../../CreditSystem/NavbarUserMenu/CreditBalanceActivatorBadge';
import CreditBalancePopoverMenuItem from '../../CreditSystem/NavbarUserMenu/CreditBalancePopoverMenuItem';
import VIPMembershipInfoMenuItem from '../../CreditSystem/NavbarUserMenu/VIPMembershipInfoMenuItem';
import type { MenuItem } from '../../Platform/Constants';
import { renderItemLabel } from '../navigationUtils';
import RoleSwitchMenuItem from '../RoleSwitch/RoleSwitchMenuItem';
import RoleSwitchModal from '../RoleSwitch/RoleSwitchModal';
import useRoleSwitch from '../RoleSwitch/useRoleSwitch';
import MenuProfileOverview from './MenuProfileOverview';
import {
  CustomPopoverStyles,
  StyledDivider,
  UserProfilePicture,
} from './UserMenu.sc';

type UserMenuProps = {
  menuItems: MenuItem[];
};

const logoutMenuItem: MenuItem = {
  to: '/logout',
  value: 'logout',
  label: {
    defaultMessage: 'Logout',
    id: 'interactive-log-out',
  },
  icon: 'ri-logout-circle-line',
};

const UserProfilePictureDefault = () => {
  const me = useSelector(getUser);
  if (!me) return null;
  return (
    <UserProfilePicture
      avatarSize={30}
      name={userName(me)}
      size={30}
      userId={me.id}
      profilePic={me.profilePic}
      alt="Profile Picture"
    />
  );
};

const RenderPictureWithNudge = (props: PropsWithChildren) => {
  const isLocationFPALEnabled = useIsMonthlyQuotaEnabled();
  const hasViewedNudge = useSelector(state => {
    const user: User = getUser(state);
    const viewedNudge = get(
      user.metadata || {},
      'empCompanyShortlistQuotaNudge',
      false
    );
    return viewedNudge;
  });
  if (!isLocationFPALEnabled || hasViewedNudge) {
    return <>{props.children}</>;
  }
  return (
    <div
      style={{
        position: 'relative',
      }}
    >
      {props.children}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 12,
          height: 12,
          backgroundColor: Red.B93,
          zIndex: 1,
          borderRadius: '50%',
          border: `2px solid ${Neutral.B100}`,
        }}
      />
    </div>
  );
};

const UserProfileBasedOnVIPMembership = () => {
  const {
    vipMembershipInfo: { isActive },
  } = useGlintsVIPMembershipInfo('cache-first');
  if (isActive) {
    return (
      <VIPProfilePicture
        profilePictureSize={30}
        vipLogoWidth={18}
        vipLogoOffset={8}
      />
    );
  }

  return <UserProfilePictureDefault />;
};

const MenuItemWrapper = styled.div`
  padding: 0 ${space8};
  background-color: ${Neutral.B100};

  :hover,
  :active {
    background-color: #f3f3f3;
  }
`;
export default function UserMenu({ menuItems }: UserMenuProps) {
  const me = useSelector(getUser);
  const roleSwitch = useRoleSwitch();
  const [popoverState, setPopoverState] = useState({
    isActive: false,
    renderKeepAliveComponents: false,
  });
  // some components in dropdown menu needs to render modals on some action
  // so we need to keep them alive to prevent unmounting them when the popover is closed
  // this is done so that we don't have to lift the state up to the parent component
  const keepAliveMenuItemPortalNode = React.useMemo(
    () => portals.createHtmlPortalNode(),
    []
  );

  const closePopover = () =>
    setPopoverState(prev => ({
      ...prev,
      isActive: false,
    }));
  const togglePopover = () => {
    setPopoverState(prev => ({
      renderKeepAliveComponents: true,
      isActive: !prev.isActive,
    }));
  };
  const { isVIPMembershipEnabled } = useGetFeatureEnabledForCountry();
  const isLocationFPALEnabled = useIsMonthlyQuotaEnabled();

  if (!me) return null;

  const activator = (
    <Flex
      style={{
        gap: space8,
        padding: `0 ${space8}`,
      }}
      alignItems="center"
      onClick={togglePopover}
    >
      <Choose>
        <When condition={isVIPMembershipEnabled}>
          <ErrorBoundaryWithCrashReporting
            fallback={<UserProfilePictureDefault />}
          >
            <Suspense fallback={<UserProfilePictureDefault />}>
              <RenderPictureWithNudge>
                <UserProfileBasedOnVIPMembership />
              </RenderPictureWithNudge>
            </Suspense>
          </ErrorBoundaryWithCrashReporting>
        </When>
        <Otherwise>
          <UserProfilePictureDefault />
        </Otherwise>
      </Choose>
      <div>
        <Flex
          style={{
            gap: space8,
          }}
        >
          <Typography
            variant="caption"
            color={Neutral.B18}
            aria-label="user-name"
          >
            {userName(me, true).length > 12
              ? truncateString(userName(me, true), 12)
              : userName(me, true)}
          </Typography>
          <Icon
            name="ri-arrow-m-down-line"
            width={18}
            height={18}
            fill={Neutral.B40}
          />
        </Flex>

        <RenderBasedOnCompanyCountry.CreditEnabledCountryOnly>
          <CreditBalanceActivatorBadge />
        </RenderBasedOnCompanyCountry.CreditEnabledCountryOnly>
      </div>
    </Flex>
  );

  return (
    <div
      style={{
        cursor: 'pointer',
      }}
    >
      <portals.InPortal node={keepAliveMenuItemPortalNode}>
        {popoverState.renderKeepAliveComponents && (
          <>
            <RenderBasedOnCompanyCountry.VIPMembershipEnabledCountryOnly>
              <MenuItemWrapper>
                <VIPMembershipInfoMenuItem closePopover={closePopover} />
              </MenuItemWrapper>
            </RenderBasedOnCompanyCountry.VIPMembershipEnabledCountryOnly>
            <RenderBasedOnCompanyCountry.CreditEnabledCountryOnly>
              <MenuItemWrapper>
                <CreditBalancePopoverMenuItem closePopover={closePopover} />
              </MenuItemWrapper>
            </RenderBasedOnCompanyCountry.CreditEnabledCountryOnly>
            <If condition={isLocationFPALEnabled}>
              <RenderBasedOnCompanyCountry.VIPMembershipEnabledCountryOnly>
                <MenuItemWrapper>
                  <CompanyShortlistQuotaMenuItem closePopover={closePopover} />
                </MenuItemWrapper>
              </RenderBasedOnCompanyCountry.VIPMembershipEnabledCountryOnly>
            </If>
          </>
        )}
      </portals.InPortal>
      <Popover
        active={popoverState.isActive}
        activator={activator}
        onClose={closePopover}
        zIndexOverride={1200}
        preferredAlignment="right"
        preferredPosition="below"
      >
        <CustomPopoverStyles />
        <div
          style={{
            margin: space8,
            marginBottom: 0,
          }}
        >
          <MenuProfileOverview />
          <StyledDivider />
        </div>
        <portals.OutPortal node={keepAliveMenuItemPortalNode} />
        {menuItems.map(item => (
          <MenuItemWrapper key={`menu-${item.value}`}>
            <BasicMenuItem item={item} onClick={closePopover} />
          </MenuItemWrapper>
        ))}
        <If condition={roleSwitch.isEnabled}>
          <MenuItemWrapper>
            <RoleSwitchMenuItem
              onClick={() => {
                closePopover();
                roleSwitch.openModal();
              }}
            />
          </MenuItemWrapper>
        </If>
        <MenuItemWrapper>
          <BasicMenuItem item={logoutMenuItem} onClick={closePopover} />
        </MenuItemWrapper>
      </Popover>
      <RoleSwitchModal
        isOpen={roleSwitch.isModalOpen}
        isSwitching={roleSwitch.isSwitching}
        onClose={roleSwitch.closeModal}
        onConfirm={roleSwitch.confirmSwitch}
      />
    </div>
  );
}

function BasicMenuItem({
  item,
  onClick,
}: {
  item: MenuItem;
  onClick: () => void;
}) {
  const intl = useIntl();

  const content = (
    <Flex
      style={{
        padding: space8,
        gap: space8,
      }}
    >
      {item.icon && <Icon name={item.icon} fill={Neutral.B40} width={24} />}
      <Typography variant="body1" color={Neutral.B18}>
        {renderItemLabel(intl, item.label)}
      </Typography>
    </Flex>
  );
  const location = useLocation();
  const isActive = location.pathname === item.to;

  const handleClick = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    onClick();
  };

  const handleClickLink = (e: {
    stopPropagation: () => void;
    preventDefault: () => void;
  }) => {
    e.stopPropagation();

    onClick();
  };

  if (isActive) {
    return (
      <div
        style={{
          cursor: 'pointer',
        }}
        onClick={handleClick}
      >
        {content}
      </div>
    );
  }
  return (
    <Link to={item.to || '/'} onClick={handleClickLink}>
      {content}
    </Link>
  );
}
